import {Trans} from '@lingui/macro';
import React, {ComponentProps, FC, useMemo, useState} from 'react';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {CustomError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {arrayToggle} from '../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {PaletteColorsEnum} from '../../../../libs/shared/ui/src/styles/paletteModel';
import {emojiToUnified} from '../../../../srcCommon/helpers/emoji/emojiHelpers';
import {useConstant} from '../../../../srcCommon/helpers/hookHelpers';
import {PaletteColorPicker} from '../../../components/common/color/paletteColorPicker';
import {TagTemplate} from '../../../components/common/dropdowns/tags/tagEditorBody';
import {TagEditorCreationError} from '../../../components/common/dropdowns/tags/tagEditorCreationError';
import {TagEditorEmoji} from '../../../components/common/dropdowns/tags/tagEditorEmoji';
import {delayAsync} from '../../../helpers/asyncHelpers';
import {getTagColorHighlight, getTagEmojiHighlight} from '../../../helpers/models/highlightHelpers';
import {HighlightTypesEnum} from '../../../models/api/highlightBaseModel';
import {InboxModel} from '../../../models/api/inboxModel';
import {SearchTagModel} from '../../../models/api/searchTagModel';
import {TagModel} from '../../../models/api/tagModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {emojiMap} from '../../../services/emojis/emojis';
import {TagHasTooManyChildren, TagNotAvailableInInboxes} from '../../../store/data/tags/saveTagActions';
import {SettingsBannersEnum, useSettingsBanners} from '../../banners/settingsBannersContext';
import {SettingsFieldsSectionTitle} from '../../common/settingsFieldsSectionTitle';
import {SettingsInboxesField} from '../../common/settingsInboxesField';
import {
  SettingsFooter,
  StyledSettingsFormContent,
  StyledSettingsFormField,
  StyledSettingsFormHeaderButtons,
  StyledSettingsFormMain,
  StyledSettingsFormTextBox,
  StyledSettingsWrapper,
} from '../../common/settingsWrapper';
import {SettingsTagsHeaderContainer} from '../settingsTagsHeaderContainer';
import {SettingsSingleTagConversationsField} from './settingsSingleTagConversationsField';
import {SettingsSingleTagHighlightField} from './settingsSingleTagHighlightField';
import {SettingsSingleTagParentTagField} from './settingsSingleTagParentTagField';
import {SettingsSingleTagWorkspaceField} from './settingsSingleTagWorkspaceField';

/*
 * Props.
 */

interface SettingsSingleTagProps {
  inboxes: ReadonlyArray<InboxModel>;
  isEditing?: boolean;
  namespace: string;
  onCancelClick: Handler;
  onDeleteClick: Handler;
  onArchiveClick: Handler;
  onSaveClick: (template: TagTemplate) => Promise<void | TagModel>;
  openParentTag: (tagId?: number) => void;
  parentTag?: TagModel;
  initialShowInCellsValue: boolean;
  initialShowInWorkspaceValue: boolean;
  shouldShowTeammatePinFeature: boolean;
  tag?: TagModel;
  teammates: ReadonlyArray<TeammateModel>;
  userId: number;
}

/*
 * Component.
 */

export const SettingsSingleTag: FC<SettingsSingleTagProps> = (props) => {
  const {
    inboxes,
    isEditing,
    namespace,
    onDeleteClick,
    onArchiveClick,
    parentTag,
    shouldShowTeammatePinFeature,
    tag,
    teammates,
    userId,
  } = props;
  const initialTagState = useConstant(() => ({
    name: tag?.name || '',
    emojiHighlight: (tag && getTagEmojiHighlight(tag)) || undefined,
    colorHighlight: (tag && getTagColorHighlight(tag)) || undefined,
    parentTags: (parentTag && [parentTag]) || [],
    showInCells: props.initialShowInCellsValue,
    showInWorkspace: props.initialShowInWorkspaceValue,
    selectedTeammateIds: undefined,
    isVisibleInAllInboxes: tag?.isVisibleInAllInboxes ?? true,
    inboxIds: tag?.inboxIds ?? [],
  }));

  const [tagName, setTagName] = useState(initialTagState.name);
  const [tagEmojiHighlight, setTagEmojiHighlight] = useState(initialTagState.emojiHighlight);
  const [tagColorHighlight, setTagColorHighlight] = useState(initialTagState.colorHighlight);
  const [parentTags, setParentTags] = useState<SearchTagModel>(initialTagState.parentTags);
  const [showInCells, setShowInCells] = useState(initialTagState.showInCells);
  const [showInPersonalWorkspaces, setShowInPersonalWorkspaces] = useState(initialTagState.showInWorkspace);
  const [selectedTeammateIds, setSelectedTeammateIds] = useState<ReadonlyArray<number> | undefined>(
    initialTagState.selectedTeammateIds,
  );
  const [tagError, setTagError] = useState<CustomError | undefined>(undefined);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(!tag);
  const [isVisibleInAllInboxes, setIsVisibleInAllInboxes] = useState<boolean>(
    initialTagState.isVisibleInAllInboxes,
  );
  const [inboxIds, setInboxIds] = useState(initialTagState.inboxIds);

  const {emoji, skinTone} = tagEmojiHighlight || {emoji: emojiMap.smile, skinTone: undefined};
  const paletteColor = tagColorHighlight || (tagEmojiHighlight ? undefined : PaletteColorsEnum.GREY);

  const {showBanner} = useSettingsBanners();

  const renderHeaderRight = () => (
    <StyledSettingsFormHeaderButtons>
      <Button
        size={VisualSizesEnum.MEDIUM}
        variant={ButtonVariantsEnum.SECONDARY_DANGER}
        color={PaletteColorsEnum.RED}
        onClick={onDeleteClick}
      >
        <Trans id="settings-deletetag">Delete tag</Trans>
      </Button>
      <Button size={VisualSizesEnum.MEDIUM} variant={ButtonVariantsEnum.SECONDARY} onClick={onArchiveClick}>
        <Trans id="settings-archivetag">Archive tag</Trans>
      </Button>
    </StyledSettingsFormHeaderButtons>
  );

  const onColorSelect: ComponentProps<typeof PaletteColorPicker>['onColorSelect'] = (color) => {
    setIsDirty(true);
    setTagColorHighlight(color);
    setTagEmojiHighlight(undefined);
  };

  const onEmojiSelect: ComponentProps<typeof TagEditorEmoji>['onEmojiSelect'] = (_shortcode, definition) => {
    setIsDirty(true);
    setTagColorHighlight(undefined);
    setTagEmojiHighlight(definition);
  };

  const buildTagTemplate = (): TagTemplate => {
    const shouldUsePinFeature = !isEditing && shouldShowTeammatePinFeature;
    const showInWorkspaces =
      shouldUsePinFeature && selectedTeammateIds?.length
        ? selectedTeammateIds.includes(props.userId)
        : showInPersonalWorkspaces;

    const baseProps = {
      name: tagName || '',
      namespace,
      parentTags,
      showInWorkspaces,
      showInCells,
      teammatesToPin: (shouldUsePinFeature && selectedTeammateIds) || [],
      isVisibleInAllInboxes,
      inboxIds,
    };

    if (tagColorHighlight) {
      return {
        ...baseProps,
        highlightColor: tagColorHighlight,
        highlightType: HighlightTypesEnum.COLOR,
      };
    }

    if (tagEmojiHighlight) {
      return {
        ...baseProps,
        highlightEmoji: emojiToUnified(tagEmojiHighlight.emoji, tagEmojiHighlight.skinTone),
        highlightType: HighlightTypesEnum.EMOJI,
      };
    }

    return {
      ...baseProps,
      highlightType: HighlightTypesEnum.NONE,
    };
  };

  const onOkClick = async () => {
    try {
      setTagError(undefined);
      setIsSaving(true);
      setIsDirty(false);
      const template = buildTagTemplate();
      await props.onSaveClick(template);
      if (isEditing) {
        showBanner({
          type: SettingsBannersEnum.SUCCESS,
          message: (
            <Trans id="settingstags-update-success">Tag &quot;{template.name}&quot; has been updated</Trans>
          ),
        });
      } else {
        showBanner({
          type: SettingsBannersEnum.SUCCESS,
          message: (
            <Trans id="settingstags-create-success">Tag &quot;{template.name}&quot; has been created</Trans>
          ),
        });
      }

      // The results of the search_tag2 route take a small bit to update, this artificial delay (sometimes) helps.
      await delayAsync(500);
      props.openParentTag(parentTag?.id);
    } catch (error) {
      if (!(error instanceof CustomError)) {
        throw error;
      }

      if (error instanceof TagNotAvailableInInboxes) {
        showBanner({
          type: SettingsBannersEnum.ERROR,
          message: <Trans id="tageditor-noinboxes">Tag must be available in at least one inbox</Trans>,
          dismissAfter: 30 * 1000,
        });
      } else if (error instanceof TagHasTooManyChildren) {
        showBanner({
          type: SettingsBannersEnum.ERROR,
          message: <Trans id="tageditor-toomanychildren">This tag cannot accept more child tags</Trans>,
          dismissAfter: 30 * 1000,
        });
      } else {
        setTagError(error);
      }

      setIsSaving(false);
      setIsDirty(true);
    }
  };

  const canSave = useMemo(() => !isSaving && Boolean(tagName && namespace), [isSaving, namespace, tagName]);

  const maybeRenderFooter = () => {
    if (!isDirty) {
      return null;
    }

    return (
      <SettingsFooter>
        <Button variant={ButtonVariantsEnum.SECONDARY} onClick={props.onCancelClick}>
          <Trans id="settingstags-cancel">Cancel</Trans>
        </Button>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onOkClick} isDisabled={!canSave}>
          {isEditing ? (
            <Trans id="settingstags-savetag">Save</Trans>
          ) : (
            <Trans id="settingstags-createtag">Create</Trans>
          )}
        </Button>
      </SettingsFooter>
    );
  };

  return (
    <StyledSettingsWrapper>
      <SettingsTagsHeaderContainer
        tagId={tag?.id}
        parentTag={props.parentTag}
        isEditingATag
        openParentTag={props.openParentTag}
        right={tag ? renderHeaderRight() : undefined}
      />
      <StyledSettingsFormMain>
        <StyledSettingsFormContent>
          <StyledSettingsFormField>
            <StyledSettingsFormTextBox
              $width={258}
              label={<Trans id="tageditor-name">Name</Trans>}
              value={tagName}
              onChange={(name) => {
                setIsDirty(true);
                setTagName(name);
              }}
              isRequired
              shouldAutoFocus
              hasError={tagError !== undefined}
              errorMessage={<TagEditorCreationError error={tagError} />}
            />
          </StyledSettingsFormField>
          <div>
            <SettingsFieldsSectionTitle>
              <Trans id="settingstags-style">Style</Trans>
            </SettingsFieldsSectionTitle>
            <SettingsSingleTagHighlightField
              paletteColor={paletteColor}
              onColorSelect={onColorSelect}
              isEmojiSelected={Boolean(tagEmojiHighlight)}
              emoji={emoji}
              skinTone={skinTone}
              onEmojiSelect={onEmojiSelect}
            />
            <SettingsSingleTagParentTagField
              namespace={namespace}
              selectedTag={parentTags[0]}
              excludeTagId={tag?.id}
              onTagSelect={(newParentTags) => {
                setIsDirty(true);
                setParentTags(newParentTags || []);
              }}
            />
          </div>
          <div>
            <SettingsFieldsSectionTitle>
              <Trans id="settingstags-access">Access</Trans>
            </SettingsFieldsSectionTitle>
            <SettingsSingleTagWorkspaceField
              namespace={namespace}
              teammates={teammates}
              selectedTeammateIds={selectedTeammateIds || [userId]}
              shouldShowTeammatePinFeature={shouldShowTeammatePinFeature}
              showInPersonalWorkspaces={showInPersonalWorkspaces}
              onChange={(bool) => {
                setIsDirty(true);
                setShowInPersonalWorkspaces(bool);
              }}
              onTeammatesChange={(newIds) => {
                setIsDirty(true);
                setSelectedTeammateIds(newIds);
                setShowInPersonalWorkspaces(newIds.includes(props.userId));
              }}
            />
            <SettingsSingleTagConversationsField
              isChecked={showInCells}
              onChange={(bool) => {
                setIsDirty(true);
                setShowInCells(bool);
              }}
            />
            <SettingsInboxesField
              subtitle={
                <Trans id="settingstags-inboxaccess-subtitle">
                  Anyone can add this tag to any conversation
                </Trans>
              }
              namespace={namespace}
              isVisibleInAllInboxes={isVisibleInAllInboxes}
              onChange={(bool) => {
                setIsDirty(true);
                setIsVisibleInAllInboxes(bool);
              }}
              inboxes={inboxes}
              onPickerHeaderChange={() => {
                setIsDirty(true);
                const isAllSelected = inboxIds.length === inboxes.length;
                setInboxIds(isAllSelected ? [] : inboxes.map((i) => i.id));
              }}
              onInboxChange={(inbox) => {
                setIsDirty(true);
                setInboxIds((prev) => arrayToggle(prev, inbox.id));
              }}
              selectedInboxIds={inboxIds}
            />
          </div>
        </StyledSettingsFormContent>
      </StyledSettingsFormMain>
      {maybeRenderFooter()}
    </StyledSettingsWrapper>
  );
};
